import { useState } from "react";
import type { ProtocolStep } from "@/mocks/protocolResponses";
import NormativeSourceAccordion from "./NormativeSourceAccordion";

interface StepCardProps {
  step: ProtocolStep;
  isLast: boolean;
}

function ProtocolStepCard({ step, isLast }: StepCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="relative flex gap-4">
      {/* Timeline connector */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-primary-500 text-white flex items-center justify-center flex-shrink-0 z-10">
          <span className="text-sm font-bold">{step.numero}</span>
        </div>
        {!isLast && (
          <div className="w-0.5 flex-1 min-h-[24px] bg-primary-200 my-1" />
        )}
      </div>

      {/* Card */}
      <div className={`flex-1 min-w-0 pb-5 ${isLast ? "" : ""}`}>
        <div className="bg-white border border-background-200/80 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-start justify-between gap-3">
              <h4 className="text-sm font-semibold text-foreground-900 leading-snug">
                {step.titulo}
              </h4>
              {step.alerta && (
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center" title="Alerta crítica en este paso">
                  <i className="ri-alert-fill text-red-600 text-xs" />
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-4 pb-3 space-y-2.5">
            <div>
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wide mb-1">Acción</p>
              <p className="text-sm text-foreground-700 leading-relaxed">{step.accion}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-foreground-400 text-sm flex-shrink-0" />
                <div>
                  <p className="text-xs text-foreground-400">Responsable</p>
                  <p className="text-sm font-medium text-foreground-800">{step.responsable}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-foreground-400 text-sm flex-shrink-0" />
                <div>
                  <p className="text-xs text-foreground-400">Plazo</p>
                  <p className="text-sm font-medium text-foreground-800">{step.plazo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <i className="ri-file-list-3-line text-foreground-400 text-sm flex-shrink-0" />
                <div>
                  <p className="text-xs text-foreground-400">Registro requerido</p>
                  <p className="text-sm font-medium text-primary-700">{step.registro_requerido}</p>
                </div>
              </div>
            </div>

            {/* Alert within step */}
            {step.alerta && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <i className="ri-error-warning-fill text-red-500 text-sm flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800 leading-relaxed">{step.alerta}</p>
              </div>
            )}
          </div>

          {/* Source accordion */}
          <div className="border-t border-background-100">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-primary-600 hover:bg-primary-50/50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-1.5">
                <i className="ri-book-open-line" />
                Ver fundamento normativo
              </span>
              {expanded ? (
                <i className="ri-arrow-up-s-line text-sm" />
              ) : (
                <i className="ri-arrow-down-s-line text-sm" />
              )}
            </button>
            {expanded && (
              <div className="px-4 pb-3">
                <NormativeSourceAccordion
                  documento={step.fuente.documento}
                  articulo={step.fuente.articulo}
                  seccion={step.fuente.seccion}
                  pagina={step.fuente.pagina}
                  fragmento={step.fuente.fragmento}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface TimelineProps {
  steps: ProtocolStep[];
}

export default function ProtocolTimeline({ steps }: TimelineProps) {
  if (steps.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
          <i className="ri-timeline-view text-primary-600 text-sm" />
        </div>
        <h3 className="text-base font-semibold text-foreground-900">Línea de Tiempo de Actuación</h3>
        <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
          {steps.length} pasos
        </span>
      </div>

      <div className="pl-1">
        {steps.map((step, i) => (
          <ProtocolStepCard
            key={step.numero}
            step={step}
            isLast={i === steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export { ProtocolStepCard };